import {
  Checkbox,
  FlexBox,
  Gender,
  JoonUIColor,
  SPACING,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import OnboardingPageContentWrapper from "./onboardingPageContentWrapper/OnboardingPageContentWrapper"
import { mergeQuestionnaireAnswers } from "./questionnaires/mergeQuestionnaireAnswers"
import QuestionnaireDisclaimer from "./questionnaires/QuestionnaireDisclaimer"
import QuestionnaireSection from "./questionnaires/QuestionnaireSection"
import { BACKGROUND_COLORS, GENDER_OPTIONS } from "../../constants"
import useMediaQuery from "../../hooks/useMediaQuery"
import useMobile from "../../hooks/useMobile"
import useOnboardContext, { ChildStep } from "../../hooks/useOnboardContext"
import { useQuestionnairesQuery } from "../../networking/queries"
import { ChildDataInterface } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { localStorageItems } from "../../util/storage"
import ChevronBackButton from "../buttons/ChevronBackButton"
import Primary3DButton from "../buttons/Primary3DButton"
import SecondaryTransparentButton from "../buttons/SecondaryTransparentButton"
import InputSectionBox from "../inputSectionBox/InputSectionBox"
import TextInputRect from "../textInputRect/TextInputRect"
import UserComponent from "../UserComponent"

// Maps a gender option's value to its translation key (GENDER_OPTIONS labels
// live in constants and are shared, so we translate at render time here).
const GENDER_LABEL_KEYS = {
  [Gender.FEMALE]: "GENDER_GIRL",
  [Gender.MALE]: "GENDER_BOY",
  [Gender.OTHER]: "GENDER_PREFER_NOT",
} as const

interface AddChildInformationProps {
  name?: string
  birthday?: dayjs.Dayjs | null
  gender?: Gender | null
  onRemoveRow: () => void
  onDoneAdding: (
    name: string,
    birthday: dayjs.Dayjs | null,
    gender?: Gender | null,
    isEditingChild?: boolean
  ) => void
  editingChildIndex?: number | null
  setEditingChildIndex: (index: number | null) => void
  setTempChildInfo: (childInfo: ChildDataInterface) => void
  childrenData?: ChildDataInterface[]
  isEditingChild?: boolean
}

function AddChildInformation({
  name: initialName,
  birthday: initialBirthday,
  gender: initialGender,
  onRemoveRow,
  onDoneAdding,
  editingChildIndex,
  setEditingChildIndex,
  setTempChildInfo,
  childrenData,
  isEditingChild,
}: AddChildInformationProps) {
  const [name, setName] = useState(initialName ?? "")
  const [month, setMonth] = useState(
    initialBirthday ? initialBirthday.format("MM") : ""
  )
  const [day, setDay] = useState(
    initialBirthday ? initialBirthday.format("DD") : ""
  )
  const [year, setYear] = useState(
    initialBirthday ? initialBirthday.format("YYYY") : ""
  )
  const [gender, setGender] = useState<Gender>(initialGender ?? Gender.FEMALE)
  const [warningText, setWarningText] = useState("")
  const { t } = useTranslation()

  const onClickDoneAdding = () => {
    const trimmedName = name.trim()
    if (trimmedName.length === 0) {
      setWarningText(t("ADD_CHILD_NAME_REQUIRED"))
      return
    }
    const hasNoDate = month === "" && day === "" && year === ""
    const hasValidDate =
      hasNoDate ||
      (month.length === 2 &&
        day.length === 2 &&
        year.length === 4 &&
        dayjs(`${year}-${month}-${day}`).isValid() &&
        dayjs(`${year}-${month}-${day}`).isBefore(dayjs()))

    if (!hasValidDate) {
      setWarningText(t("ADD_CHILD_INVALID_BIRTHDATE"))
      return
    }

    const birthday = hasNoDate ? null : dayjs(`${year}-${month}-${day}`)

    trackAnalyticEvent(ANALYTIC_EVENTS.INPUT_CHILD_INFORMATION, {
      gender: gender,
      birthdate: birthday?.format("MM/DD/YYYY") ?? null,
    })

    onDoneAdding(trimmedName, birthday, gender, isEditingChild)
  }

  const onChangeDateOfBirth = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, "")
    if (value.length > 2) {
      value = value.slice(0, 2) + "/" + value.slice(2)
    }
    if (value.length > 5) {
      value = value.slice(0, 5) + "/" + value.slice(5)
    }
    const [newMonth, newDay, newYear] = value.split("/")
    setMonth(newMonth || "")
    setDay(newDay || "")
    setYear(newYear || "")
  }

  return (
    <InputSectionBox
      style={{
        padding: 0,
        border: "none",
        background: "transparent",
        boxShadow: "none",
        overflow: "visible",
      }}
    >
      <FlexBox direction="column" gap={SPACING.space6}>
        <FlexBox direction="column" gap={SPACING.space1}>
          <Typography variant="body">
            {t("ADD_CHILD_FIRST_NAME_LABEL")}
          </Typography>
          <TextInputRect
            placeholder={t("ADD_CHILD_FIRST_NAME_PLACEHOLDER")}
            onChange={(event: any) => setName(event.target.value)}
            value={name}
            name="firstName"
            fullWidth
          />
        </FlexBox>
        <FlexBox direction="column" gap={SPACING.space1}>
          <Typography variant="body">
            {t("ADD_CHILD_GENDER_LABEL")}{" "}
            <Typography variant="bodyXSmall" color={JoonUIColor.text.secondary}>
              {t("ONBOARDING_OPTIONAL")}
            </Typography>
          </Typography>
          <FlexBox
            direction="column"
            wrap={false}
            fullWidth
            style={{
              background: JoonUIColor.background.primaryNeutral,
              borderRadius: SPACING.space2,
              border: `1px solid ${JoonUIColor.border.default}`,
            }}
          >
            {GENDER_OPTIONS.map((option, index) => (
              <button
                key={index}
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  gap: SPACING.space2,
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: `1px solid ${JoonUIColor.border.default}`,
                  ...(index === 0 && {
                    borderTop: "none",
                    borderTopLeftRadius: SPACING.space2,
                    borderTopRightRadius: SPACING.space2,
                  }),
                  ...(index === GENDER_OPTIONS.length - 1 && {
                    borderBottom: "none",
                    borderBottomLeftRadius: SPACING.space2,
                    borderBottomRightRadius: SPACING.space2,
                  }),
                  padding: SPACING.space3,
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  setGender(option.value as Gender)
                }}
              >
                <Typography variant="bodySmall" textAlign="left">
                  {t(
                    GENDER_LABEL_KEYS[
                      option.value as keyof typeof GENDER_LABEL_KEYS
                    ]
                  )}
                </Typography>
                <Checkbox
                  selected={option.value === gender}
                  inputType="radio"
                  name={option.label}
                  onChange={() => {}}
                  hideBorder
                />
              </button>
            ))}
          </FlexBox>
        </FlexBox>
        <FlexBox direction="column" gap={SPACING.space1}>
          <Typography variant="body">
            {t("ADD_CHILD_BIRTHDAY_LABEL")}{" "}
            <Typography variant="bodyXSmall" color={JoonUIColor.text.secondary}>
              {t("ADD_CHILD_BIRTHDAY_OPTIONAL")}
            </Typography>
          </Typography>
          <TextInputRect
            name="date-of-birth"
            placeholder="MM/DD/YYYY"
            value={`${month}${day ? "/" + day : ""}${year ? "/" + year : ""}`}
            onChange={onChangeDateOfBirth}
            maxLength={10}
            fullWidth
          />
        </FlexBox>
        <FlexBox
          justify="center"
          align="center"
          direction="column"
          gap={SPACING.space2}
        >
          {(warningText.length > 0 ||
            (childrenData && childrenData.length === 0)) && (
            <Typography
              variant="bodySmall"
              textAlign="center"
              color={
                warningText.length > 0
                  ? JoonUIColor.semantic.alert
                  : JoonUIColor.text.primary
              }
              style={{ marginTop: SPACING.space2 }}
            >
              {warningText.length > 0
                ? warningText
                : t("ADD_CHILD_ADD_MORE_LATER")}
            </Typography>
          )}

          <FlexBox
            direction="column"
            gap={SPACING.space3}
            wrap={false}
            align="center"
            fullWidth
          >
            <Primary3DButton
              text={t("ADD_CHILD_SAVE")}
              onClick={onClickDoneAdding}
            />
            <FlexBox
              direction="row"
              align="center"
              wrap={false}
              gap={SPACING.space2}
              fullWidth
            >
              {editingChildIndex !== -1 && (
                <SecondaryTransparentButton
                  text={
                    <Typography
                      variant="body"
                      color={JoonUIColor.semantic.alert}
                    >
                      {t("ADD_CHILD_REMOVE")}
                    </Typography>
                  }
                  onClick={onRemoveRow}
                  style={{
                    width: "50%",
                    height: "50px",
                    padding: SPACING.space3,
                    borderRadius: SPACING.space2,
                    border: `1px solid ${JoonUIColor.semantic.alert}`,
                  }}
                />
              )}
              {editingChildIndex !== null &&
                editingChildIndex !== undefined &&
                childrenData &&
                childrenData.length !== 0 && (
                  <SecondaryTransparentButton
                    onClick={() => {
                      if (editingChildIndex >= 0)
                        setTempChildInfo({} as ChildDataInterface)
                      setEditingChildIndex(null)
                    }}
                    text={t("ADD_CHILD_CANCEL")}
                    style={{ width: editingChildIndex !== -1 ? "50%" : "100%" }}
                  />
                )}
            </FlexBox>
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </InputSectionBox>
  )
}

function OnboardAddChildrenSection() {
  const {
    previousStep,
    setCreatingPlan,
    nextStep,
    setParentData,
    parentData,
    childStep,
    setChildStep,
  } = useOnboardContext()
  const [childrenData, setChildrenData] = useState(parentData.childrenData)
  const [editingChildIndex, setEditingChildIndex] = useState<number | null>(
    null
  )
  const [tempChildInfo, setTempChildInfo] = useState<ChildDataInterface>(
    {} as ChildDataInterface
  )
  const { data: questionsResponse } = useQuestionnairesQuery()
  const isMobile = useMobile()
  const isSmallMobile = useMediaQuery("(max-width: 500px)")
  const { t } = useTranslation()
  useEffect(() => {
    if (childrenData.length === 0 && editingChildIndex === null)
      setEditingChildIndex(-1)
  }, [childrenData, editingChildIndex])

  if (!questionsResponse) return null
  const { childQuestions: questions } = questionsResponse

  const onDoneAdding = (
    name: string,
    birthday: dayjs.Dayjs | null,
    gender?: Gender | null,
    isEditingChild?: boolean
  ) => {
    const updatedTempChildInfo = {
      first_name: name,
      birthdate: birthday && birthday.unix(),
      gender: gender ?? Gender.OTHER,
      questionAnswerMapping: [],
    }
    setTempChildInfo(updatedTempChildInfo as ChildDataInterface)
    const therapistCode = localStorage.getItem(localStorageItems.therapistCode)
    if (isEditingChild || !!therapistCode) {
      finishQuestionnaire(updatedTempChildInfo as ChildDataInterface)
    } else {
      setChildStep(ChildStep.QuestionnaireDisclaimer)
    }
  }

  const onRemoveRow = () => {
    if (editingChildIndex == null) return
    if (editingChildIndex >= 0) {
      const newChildrenData = [...childrenData]
      newChildrenData.splice(editingChildIndex, 1)
      setChildrenData(newChildrenData)
    }
    setEditingChildIndex(null)
    setTempChildInfo({} as ChildDataInterface)
  }

  const finishQuestionnaire = (data: ChildDataInterface) => {
    if (editingChildIndex == null) return

    // If user said their child has ABA therapist, we want to show the ABA invite section
    const childHasABAAnswer = 632
    if (
      Object.values(data.questionAnswerMapping).some((answerIds) =>
        answerIds.includes(childHasABAAnswer)
      )
    ) {
      localStorage.setItem(localStorageItems.showABAInviteSection, "true")
      trackAnalyticEvent(ANALYTIC_EVENTS.ABA_QUESTION_ANSWERED)
    }

    const gender = data.gender
      ? data.gender
      : tempChildInfo.gender === null || tempChildInfo.gender === undefined
        ? null
        : tempChildInfo.gender

    const newChild = {
      first_name: data.first_name ? data.first_name : tempChildInfo.first_name,
      birthdate: data.birthdate ? data.birthdate : tempChildInfo.birthdate,
      gender: gender,
      questionAnswerMapping: data.questionAnswerMapping,
    }
    if (editingChildIndex < 0) {
      // Add new child
      const newChildrenData = [...childrenData, newChild]
      setChildrenData(newChildrenData)
      setTempChildInfo({} as ChildDataInterface)
    } else {
      // Update existing child
      const newChildrenData = [...childrenData]
      newChildrenData[editingChildIndex] = newChild
      setChildrenData(newChildrenData)
    }
    const mergedAnswers = mergeQuestionnaireAnswers(data.questionAnswerMapping)

    localStorage.setItem(
      localStorageItems.parentQuestionnaireAnswers,
      JSON.stringify(mergedAnswers)
    )
    requestAnimationFrame(() => {
      setEditingChildIndex(null)
      setChildStep(ChildStep.ChildInfo)
    })
  }

  if (childStep === ChildStep.ChildInfo) {
    return (
      <OnboardingPageContentWrapper
        backgroundOnDesktop={BACKGROUND_COLORS.lightBlueBg}
        paddingTop={
          (editingChildIndex === null || editingChildIndex === -1) &&
          childrenData.length === 0
            ? SPACING.space4
            : SPACING.space12
        }
        style={{
          position: "relative",
          height: isMobile ? "100dvh" : "80dvh",
          minHeight: isMobile ? "100dvh" : "780px",
        }}
      >
        <ChevronBackButton
          onClick={() => {
            if (editingChildIndex === null || childrenData.length === 0)
              previousStep()
            else {
              if (editingChildIndex >= 0)
                setTempChildInfo({} as ChildDataInterface)
              setEditingChildIndex(null)
            }
          }}
        />
        <FlexBox direction="column" align="center" gap={SPACING.space4}>
          {(editingChildIndex === null || childrenData.length === 0) && (
            <div
              style={{
                height: childrenData.length === 0 ? "110px" : "200px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={
                  childrenData.length === 0
                    ? "images/ahoot_go_pie.png"
                    : "images/gifs/onboarding-tree-clouds-gif.gif"
                }
                alt={childrenData.length === 0 ? "Ahoot Go Pie" : "treehouse"}
                style={{
                  width:
                    childrenData.length === 0
                      ? "min(40%, 100px)"
                      : isSmallMobile
                        ? "160px"
                        : "200px",
                }}
              />
            </div>
          )}
          <Typography variant="h2Serif" textAlign="center">
            {editingChildIndex !== null && editingChildIndex !== -1
              ? t("ADD_CHILD_TITLE_UPDATE")
              : childrenData.length === 0
                ? t("ADD_CHILD_TITLE_FIRST")
                : editingChildIndex === null
                  ? t("ADD_CHILD_TITLE_YOUR_CHILDREN")
                  : t("ADD_CHILD_TITLE_ADD")}
          </Typography>
          <FlexBox gap={SPACING.space2} wrap={false} direction="column">
            {childrenData.map((childData, index) => {
              const isEditingChild = index === editingChildIndex
              return (
                <FlexBox key={index}>
                  {isEditingChild ? (
                    <AddChildInformation
                      name={childData.first_name}
                      birthday={
                        childData.birthdate
                          ? dayjs.unix(childData.birthdate)
                          : null
                      }
                      gender={childData.gender}
                      onDoneAdding={onDoneAdding}
                      onRemoveRow={onRemoveRow}
                      setEditingChildIndex={setEditingChildIndex}
                      setTempChildInfo={setTempChildInfo}
                      childrenData={childrenData}
                      editingChildIndex={editingChildIndex}
                      isEditingChild={isEditingChild}
                    />
                  ) : editingChildIndex === null ? (
                    <UserComponent
                      name={childData.first_name}
                      index={index}
                      type={UserRole.CHILD}
                      editChild={() => setEditingChildIndex(index)}
                    />
                  ) : (
                    <></>
                  )}
                </FlexBox>
              )
            })}
            {editingChildIndex === -1 && (
              <AddChildInformation
                name={tempChildInfo.first_name}
                birthday={
                  tempChildInfo.birthdate
                    ? dayjs.unix(tempChildInfo.birthdate)
                    : null
                }
                gender={tempChildInfo.gender}
                onDoneAdding={onDoneAdding}
                onRemoveRow={onRemoveRow}
                setEditingChildIndex={setEditingChildIndex}
                setTempChildInfo={setTempChildInfo}
                childrenData={childrenData}
                editingChildIndex={editingChildIndex}
                isEditingChild={false}
              />
            )}
          </FlexBox>
        </FlexBox>
        <FlexBox direction="column" gap={SPACING.space3}>
          {editingChildIndex == null && (
            <>
              <Typography variant="bodySmall" textAlign="center">
                {t("ADD_CHILD_ADD_MORE_LATER")}
              </Typography>
              {childrenData.length > 0 && (
                <Primary3DButton
                  fullWidth
                  onClick={() => {
                    if (!localStorage.getItem(localStorageItems.therapistCode))
                      setCreatingPlan(true)
                    nextStep()
                    trackAnalyticEvent(ANALYTIC_EVENTS.ADD_CHILDREN)
                    setParentData("childrenData", childrenData)
                  }}
                  text={t("ONBOARDING_CONTINUE")}
                />
              )}
              <SecondaryTransparentButton
                text={t("ADD_CHILD_ADD_ANOTHER")}
                onClick={() => {
                  setTimeout(
                    () => window.scrollTo(0, document.body.scrollHeight),
                    100
                  )
                  trackAnalyticEvent(ANALYTIC_EVENTS.INPUT_CHILD_INFO_INTENT)
                  setTempChildInfo({} as ChildDataInterface)
                  setEditingChildIndex(-1)
                }}
                opaque
              />
            </>
          )}
          <Typography variant="bodyXSmall" textAlign="center">
            {t("ONBOARDING_ANSWERS_PRIVATE")}
          </Typography>
        </FlexBox>
      </OnboardingPageContentWrapper>
    )
  } else if (childStep === ChildStep.QuestionnaireDisclaimer) {
    return (
      <QuestionnaireDisclaimer
        childName={tempChildInfo?.first_name}
        onConfirm={() => setChildStep(ChildStep.Questionnaire)}
      />
    )
  } else if (childStep === ChildStep.Questionnaire) {
    return (
      <QuestionnaireSection
        questions={questions}
        onConfirm={finishQuestionnaire}
        previousStep={() => setChildStep(ChildStep.ChildInfo)}
      />
    )
  } else return <div></div>
}

export default OnboardAddChildrenSection
